import { useEffect, useRef, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { getFormFields, getForms } from "../../../javascript/api/forms";

const OrderByFilter = ({ onChange, clearFilter, disabled, formIds = [], formOptions = [], accountId, value }) => {
    const [selectedOrderByValue, setSelectedOrderByValue] = useState('');
    const [orderByOptions, setOrderByOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [menuWidth, setMenuWidth] = useState('auto');
    const formControlRef = useRef(null);
    const valueRef = useRef(value);

    useEffect(() => {
        valueRef.current = value;
        setSelectedOrderByValue(value ?? '');
    }, [value]);

    useEffect(() => {
        if (clearFilter) {
            setSelectedOrderByValue('');
            onChange(null);
        }
    }, [clearFilter, onChange]);

    const handleOrderByChange = (event) => {
        const newValue = event.target.value;

        setSelectedOrderByValue(newValue);
        onChange(newValue);
    };

    const handleOpen = () => {
        if (formControlRef.current) {
            setMenuWidth(formControlRef.current.offsetWidth);
        }
    };

    useEffect(() => {
        if (!formIds?.length) {
            setOrderByOptions([]);
            setSelectedOrderByValue('');
            return;
        }

        const loadData = async () => {
            try {
                setLoading(true);

                const defaultOptions = [
                    { label: "Data criação", value: -2 },
                    { label: "Data sincronização", value: -1 },
                ];

                let resolvedFormOptions = formOptions;
                if (!formOptions.length) {
                    const response = await getForms({ accountId, ids: formIds });
                    resolvedFormOptions = response.results
                        .filter(Boolean)
                        .map((form) => ({ label: form.name, value: form.id }));
                }

                const fieldResponses = await Promise.all(
                    formIds.map((formId) => getFormFields({ accountId, id: formId }))
                );

                const allFields = fieldResponses
                    .flatMap((response, index) => {
                        const formName = resolvedFormOptions.find((f) => f.value === formIds[index])?.label || '';
                        return response.results.map((field) => ({
                            label: `${field.name} - ${formName}`,
                            value: field.id,
                        }));
                    })
                    .sort((a, b) => a.label.localeCompare(b.label));
    
                const allOptions = [...defaultOptions, ...allFields];
                setOrderByOptions(allOptions);

                const currentValue = valueRef.current;
                const valueStillExists = allOptions.some(opt => String(opt.value) === String(currentValue));
                if (currentValue && !valueStillExists) {
                    setSelectedOrderByValue('');
                    onChange(null);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [formIds, formOptions, accountId]);

    return (
        <FormControl ref={formControlRef} sx={{ minWidth: '270px', width: '100%' }}>
            <InputLabel
                id="order-by-select-label"
                shrink={Boolean(selectedOrderByValue) || selectedOrderByValue === ''}
                disabled={disabled}
            >
                Ordenar por
            </InputLabel>
            <Select
                onOpen={handleOpen}
                MenuProps={{
                    PaperProps: {
                        sx: { width: menuWidth, maxWidth: menuWidth },
                    },
                }}
                value={selectedOrderByValue}
                label="Ordenar por"
                displayEmpty
                disabled={disabled || !formIds}
                onChange={handleOrderByChange}
                renderValue={(selected) => {
                    if (loading && selected) return "Carregando...";
                    if (!selected) return '-';

                    const label = orderByOptions.find(opt =>
                        String(opt.value) === String(selected)
                    )?.label;

                    return (
                        <Typography noWrap>{label}</Typography>
                    );
                }}
            >
                {orderByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'block',
                    }}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default OrderByFilter;